import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  User,
  Brain,
  CreditCard,
  AlertTriangle,
  Save,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { useCredits } from '@/features/dashboard/hooks/useCredits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { AIModel, Transaction, TransactionType } from '@/types/database';

// =========================================
// Transaction hooks
// =========================================

function useTransactions(limit: number = 50) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['transactions', user?.id, limit],
    queryFn: async (): Promise<Transaction[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as Transaction[];
    },
    enabled: !!user,
  });
}

// =========================================
// Helpers
// =========================================

function getTransactionIcon(type: TransactionType) {
  switch (type) {
    case 'purchase':
      return <ArrowUpRight className="h-4 w-4 text-emerald-500" />;
    case 'usage':
      return <ArrowDownRight className="h-4 w-4 text-rose-500" />;
    case 'refund':
      return <RotateCcw className="h-4 w-4 text-amber-500" />;
    case 'bonus':
      return <Gift className="h-4 w-4 text-indigo-500" />;
  }
}

function getTransactionBadge(type: TransactionType) {
  const variants: Record<TransactionType, 'default' | 'destructive' | 'secondary' | 'outline'> = {
    purchase: 'default',
    usage: 'destructive',
    refund: 'secondary',
    bonus: 'outline',
  };

  return (
    <Badge variant={variants[type]} className="text-xs capitalize">
      {type}
    </Badge>
  );
}

// =========================================
// Settings Page
// =========================================

export function Settings() {
  const { user, profile, setProfile } = useAuthStore();
  const { data: credits } = useCredits();
  const { data: transactions, isLoading: txLoading } = useTransactions();

  const [fullName, setFullName] = useState(profile?.full_name ?? '');
  const [aiModel, setAiModel] = useState<AIModel>(profile?.preferred_ai_model ?? 'gpt-4-turbo');
  const [saving, setSaving] = useState(false);

  // Sync local state when profile loads asynchronously
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setAiModel(profile.preferred_ai_model ?? 'gpt-4-turbo');
    }
  }, [profile]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
      if (profile) setProfile({ ...profile, full_name: fullName.trim() || null });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModel = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          preferred_ai_model: aiModel,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
      if (profile) setProfile({ ...profile, preferred_ai_model: aiModel });
      toast.success('AI model preference saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    try {
      const { error: rpcError } = await supabase.rpc('delete_user_account');
      if (rpcError) throw rpcError;

      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      toast.success('Your account and all associated data have been permanently deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setDeleteOpen(false);
      setDeleteConfirm('');
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your profile, preferences, and account</p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            AI Model
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Transactions
          </TabsTrigger>
          <TabsTrigger value="danger" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Danger
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email ?? ''}
                  disabled
                  className="bg-slate-50"
                />
                <p className="text-xs text-slate-400">Email cannot be changed</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="space-y-2">
                <Label>Credits Balance</Label>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-indigo-500" />
                  <span className="text-2xl font-bold text-indigo-600">
                    {credits ?? 0}
                  </span>
                  <span className="text-slate-500">credits remaining</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Member Since</Label>
                <p className="text-sm text-slate-600">
                  {profile?.created_at
                    ? format(new Date(profile.created_at), 'MMMM d, yyyy')
                    : '—'}
                </p>
              </div>

              <Separator />

              <Button onClick={handleSaveProfile} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Profile
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Model Tab */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle>AI Model Preference</CardTitle>
              <CardDescription>
                Choose the default AI model for evaluations and chat
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="aiModel">Default Model</Label>
                <Select
                  value={aiModel}
                  onValueChange={(v) => setAiModel(v as AIModel)}
                >
                  <SelectTrigger className="w-full max-w-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4-turbo">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">GPT-4 Turbo</span>
                        <Badge variant="outline" className="text-xs">
                          OpenAI
                        </Badge>
                      </div>
                    </SelectItem>
                    <SelectItem value="claude-3.5-sonnet">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Claude 3.5 Sonnet</span>
                        <Badge variant="outline" className="text-xs">
                          Anthropic
                        </Badge>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <h4 className="text-sm font-medium text-slate-900">Model Comparison</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <p className="font-medium text-slate-700">GPT-4 Turbo</p>
                    <ul className="text-slate-500 space-y-0.5">
                      <li>Best for structured analysis</li>
                      <li>Strong passage extraction</li>
                      <li>128k context window</li>
                    </ul>
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-slate-700">Claude 3.5 Sonnet</p>
                    <ul className="text-slate-500 space-y-0.5">
                      <li>Excellent reasoning</li>
                      <li>Nuanced explanations</li>
                      <li>200k context window</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Separator />

              <Button onClick={handleSaveModel} disabled={saving}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save Preference
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions">
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>Your credit purchases and usage</CardDescription>
            </CardHeader>
            <CardContent>
              {txLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : !transactions?.length ? (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No transactions yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Purchase credits to get started
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between py-3 px-4 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {getTransactionIcon(tx.type)}
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {tx.description ?? tx.type}
                          </p>
                          <p className="text-xs text-slate-400">
                            {format(new Date(tx.created_at), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getTransactionBadge(tx.type)}
                        <span
                          className={`text-sm font-mono font-medium ${
                            tx.amount > 0
                              ? 'text-emerald-600'
                              : 'text-rose-600'
                          }`}
                        >
                          {tx.amount > 0 ? '+' : ''}
                          {tx.amount}
                        </span>
                        <span className="text-xs text-slate-400">
                          bal: {tx.balance_after}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Danger Zone Tab */}
        <TabsContent value="danger">
          <Card className="border-rose-200">
            <CardHeader>
              <CardTitle className="text-rose-700">Danger Zone</CardTitle>
              <CardDescription>
                Irreversible actions — proceed with caution
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-rose-900">
                      Delete Account
                    </h4>
                    <p className="text-sm text-rose-700 mt-1">
                      Permanently delete your account and all associated data.
                      This action cannot be undone.
                    </p>
                  </div>
                  <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        Delete Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Are you absolutely sure?</DialogTitle>
                        <DialogDescription>
                          This will permanently delete your account, all sources,
                          evaluations, and data. Any remaining credits will be
                          lost. Type <strong>DELETE</strong> to confirm.
                        </DialogDescription>
                      </DialogHeader>
                      <Input
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        placeholder='Type "DELETE" to confirm'
                      />
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setDeleteOpen(false);
                            setDeleteConfirm('');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          disabled={deleteConfirm !== 'DELETE'}
                          onClick={handleDeleteAccount}
                        >
                          Delete My Account
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
